import { useQuery } from '@tanstack/react-query';

import { getParentByStudio } from '~entities/parent/api/get-parent-by-studio';
import type { OnlinePaymentMethod } from '~entities/payment/api/get-allowed-payment-methods';
import { listParentSquareSavedCards } from '~entities/payment/api/square-payment-methods';
import { fetchStudioDefaultPaymentProviderAPI } from '~entities/studios/api/get-studio-default-payment-provider';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';
import { isSquarePaymentProvider } from '~shared/lib/square-web-payments';

export interface SavedPaymentMethod {
  id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  bankName?: string;
  bsbNumber?: string;
  isDefault?: boolean;
}

const resolveSquareParentId = async (params: {
  email: string;
  parentId?: string;
}): Promise<string | undefined> => {
  const explicitParentId = params.parentId?.trim();
  if (explicitParentId) {
    return explicitParentId;
  }

  const email = params.email.trim().toLowerCase();
  if (!email) {
    return undefined;
  }

  const result = await getParentByStudio({
    search: email,
    page: 1,
    limit: 25,
  });

  return result.data.find(
    (parent) => parent.email?.trim().toLowerCase() === email,
  )?._id;
};

/**
 * Saved payment methods for a parent, filtered to studio-allowed types.
 * Stripe uses GHL locationId + email. Square Cards API needs a parent id;
 * callers that only have email (event enroll) are resolved by studio search.
 */
export const getParentSavedPaymentMethods = async (params: {
  email: string;
  locationId: string;
  parentId?: string;
}): Promise<SavedPaymentMethod[]> => {
  const { paymentProvider } = await fetchStudioDefaultPaymentProviderAPI();
  if (isSquarePaymentProvider(paymentProvider)) {
    const parentId = await resolveSquareParentId(params);
    if (!parentId) {
      return [];
    }
    return listParentSquareSavedCards(parentId);
  }

  const response = await baseAPI.get<
    SavedPaymentMethod[] | { paymentMethodTypes?: string[] }
  >('/payment-processor/allowed-payment-methods', {
    params: {
      locationId: params.locationId,
      email: params.email,
    },
  });

  // Without email the API returns config; with email it returns an array.
  return Array.isArray(response.data) ? response.data : [];
};

export const useGetParentSavedPaymentMethods = (params: {
  email?: string;
  locationId?: string;
  parentId?: string;
}) => {
  const email = params.email?.trim() ?? '';
  const locationId = params.locationId?.trim() ?? '';
  const parentId = params.parentId?.trim() ?? '';

  return useQuery({
    queryKey: [KEYS.PAYMENT_METHOD, 'saved', locationId, email, parentId],
    queryFn: () =>
      getParentSavedPaymentMethods({
        email,
        locationId,
        parentId,
      }),
    enabled: !!email && !!locationId,
  });
};

/** Prefer default PM of the selected type; otherwise first match. */
export const pickPaymentMethodId = (
  methods: SavedPaymentMethod[] | undefined,
  paymentMethod: OnlinePaymentMethod | undefined,
): string | undefined => {
  if (!methods?.length || !paymentMethod) {
    return undefined;
  }

  const matchesType = (pm: SavedPaymentMethod) => {
    if (paymentMethod === 'card') {
      return pm.type === 'card' || pm.type === 'link';
    }
    return pm.type === paymentMethod;
  };

  const matching = methods.filter(matchesType);
  if (matching.length === 0) {
    return undefined;
  }

  return (matching.find((pm) => pm.isDefault) ?? matching[0])?.id;
};
